// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Hook for creating initial true-up.
 * Initial true-up establishes Hardfin as an authoritative subledger for new organizations
 * by creating a one-time true-up journal entry (JE0) to align the user's GL with
 * Hardfin's computed balances.
 */
import { createInitialTrueUp } from '@/accounting/api/create-initial-true-up';
import {
  CreateInitialTrueUpRequest,
  InitialTrueUpCloseRunResponse,
} from '@/accounting/api/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Query key constants for initial true-up queries.
 * Uses the 'accounting' namespace to align with existing accounting query keys.
 */

interface UseCreateInitialTrueUpOptions {
  onSuccess?: (closeRun: InitialTrueUpCloseRunResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * A React hook that creates a new initial true-up close run.
 * This generates the onboarding journal entry (JE0) that aligns the user's GL
 * with Hardfin's computed balances.
 *
 * @param options.onSuccess - Callback fired when the initial true-up is created successfully
 * @param options.onError - Callback fired when the mutation fails
 * @returns Mutation object for creating initial true-up
 */
export function useCreateInitialTrueUp(
  options?: UseCreateInitialTrueUpOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateInitialTrueUpRequest) => {
      return createInitialTrueUp(request);
    },
    onSuccess: async ({ initialTrueUpCloseRun }) => {
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'initial-true-up'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'initial-true-ups'],
      });

      options?.onSuccess?.(initialTrueUpCloseRun);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}
